import {
  COUNTRY_PRESETS,
  type CountryCode, currencyInfo } from '@alexiarest/shared';

/**
 * Formateo unico para toda la app. Regla de oro: ninguna pantalla puede
 * mostrar NaN, Infinity ni undefined, asi que todo pasa por `safe()`.
 *
 * Multi-mercado: los importes se formatean en la moneda del tenant.
 * `configureMoney()` se llama desde el Layout cuando llega el tenant y
 * reconstruye los formateadores; los numeros y fechas siguen en es-ES,
 * que es el idioma del producto.
 */

/**
 * Locale de las CIFRAS (separador decimal y de miles). Lo fija el pais del
 * restaurante (mejora 45 de Alex, 5 sep 2026): una cuenta de Estados Unidos
 * ve «1,234.56» en todas partes, incluido el escaner; una espanyola,
 * «1.234,56». Va por pais y no por moneda, como pidio Alex. El idioma de la
 * interfaz sigue siendo espanyol.
 */
let LOCALE = 'es-ES';
let SEPARADOR_DECIMAL = ',';

/**
 * Fija el locale de las cifras a partir del pais: el de la moneda de ese
 * pais en COUNTRY_PRESETS (Estados Unidos → es-US, con punto decimal). Si el
 * pais es «Otro», manda la moneda del restaurante. Devuelve true si cambia.
 */
export function configureLocale(country: string | undefined, currency: string | undefined): boolean {
  const preset = country && country in COUNTRY_PRESETS ? COUNTRY_PRESETS[country as CountryCode] : undefined;
  const moneda = preset && country !== 'OTHER' ? preset.currency : (currency ?? 'EUR');
  const next = currencyInfo(moneda).locale;
  if (next === LOCALE) return false;
  LOCALE = next;
  SEPARADOR_DECIMAL =
    new Intl.NumberFormat(LOCALE).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? ',';
  return true;
}

export function activeLocale(): string {
  return LOCALE;
}

/** Separador decimal del locale activo: «,» en Espanya, «.» en Estados Unidos. */
export function decimalSeparator(): string {
  return SEPARADOR_DECIMAL;
}

/**
 * Un numero para un campo de texto editable, con el separador decimal del
 * locale y sin separador de miles (un campo con «1.234,5» no se parsea bien).
 */
export function formatDecimalInput(value: number, maxDecimals = 3): string {
  const rounded = Number(value.toFixed(maxDecimals));
  return String(rounded).replace('.', decimalSeparator());
}

/** Convierte cualquier cosa en un numero finito utilizable. */
export function safe(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  // Normaliza el -0 para que nunca se imprima "-0,00 €".
  return n === 0 ? 0 : n;
}

interface MoneyFormatters {
  currency: string;
  cents: Intl.NumberFormat;
  round: Intl.NumberFormat;
  precise: Intl.NumberFormat;
}

// Agrupamos siempre los miles, tambien en cifras de cuatro digitos: en una
// columna de importes alineada a la derecha la consistencia se lee mejor.
function buildFormatters(code: string): MoneyFormatters {
  const info = currencyInfo(code);
  const make = (min: number, max: number): Intl.NumberFormat => {
    try {
      return new Intl.NumberFormat(info.locale, {
        style: 'currency',
        currency: info.code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: min,
        maximumFractionDigits: max,
        useGrouping: true,
      });
    } catch {
      // Un codigo ISO corrupto no puede tumbar la app entera.
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: min,
        maximumFractionDigits: max,
        useGrouping: true,
      });
    }
  };
  return {
    currency: info.code,
    cents: make(info.decimals, info.decimals),
    round: make(0, 0),
    // Costes unitarios (€/g): con 4 decimales aunque la moneda vaya sin
    // decimales, porque un gramo de azafran en pesos tambien tiene coma.
    precise: make(info.decimals === 0 ? 0 : 4, 4),
  };
}

let money = buildFormatters('EUR');

/**
 * Fija la moneda activa de la app. La llama el Layout al cargar el tenant
 * (y al guardarlo en Ajustes). Devuelve true si la moneda ha cambiado,
 * para que quien llama pueda forzar un re-render.
 */
export function configureMoney(currency: string | undefined): boolean {
  const next = currencyInfo(currency).code;
  if (next === money.currency) return false;
  money = buildFormatters(next);
  return true;
}

/** Moneda activa (codigo ISO), por si una pantalla la necesita. */
export function activeCurrency(): string {
  return money.currency;
}

/** Simbolo de la moneda activa (€, $, S/), para sufijos de formularios. */
export function currencySymbol(): string {
  const part = money.cents
    .formatToParts(0)
    .find((p) => p.type === 'currency');
  return part?.value ?? money.currency;
}

/** Importe con decimales de la moneda: 1.234,56 € — $12.500 en CLP/COP. */
export function formatMoney(value: unknown): string {
  return money.cents.format(safe(value));
}

/** Importe redondeado a unidades enteras, para titulares grandes. */
export function formatMoneyShort(value: unknown): string {
  return money.round.format(safe(value));
}

/** Coste unitario fino: 0,0186 € (util en €/g y €/ml). */
export function formatUnitCost(value: unknown): string {
  return money.precise.format(safe(value));
}

/**
 * Coste por unidad con la unidad detras: "18,70 €/kg", "0,0186 €/g".
 *
 * Los decimales se ajustan al tamanyo del numero: en euros por kilo cuatro
 * decimales son ruido ("18,7000 €/kg" no dice mas que "18,70 €/kg"), pero en
 * euros por gramo son imprescindibles para no enseñar "0,00 €/g".
 */
export function formatCostPerUnit(value: unknown, unit: string): string {
  const n = safe(value);
  const formato = Math.abs(n) >= 1 ? money.cents : money.precise;
  return `${formato.format(n)}/${unit}`;
}

/** Porcentaje ya expresado en base 100 (28.4 -> "28,4 %"). */
export function formatPct(value: unknown, decimals = 1): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(safe(value)) + ' %';
}

/** Porcentaje expresado en tanto por uno (0.10 -> "10 %"). */
export function formatRate(value: unknown, decimals = 0): string {
  return formatPct(safe(value) * 100, decimals);
}

/**
 * Porcentaje TAL Y COMO esta configurado, sin redondear ni rellenar ceros:
 * 8,25 sale "8,25 %" y 10 sale "10 %".
 *
 * `formatPct` fija los decimales, asi que un IVA general del 8,25 % salia como
 * "8 %" en las ayudas y parecia que Alexia estaba usando otro tipo del que
 * tienes puesto (lo vio Alex el 1 de septiembre). Para un valor que el usuario
 * ha escrito el, hay que devolverselo igual.
 */
export function formatPctExacto(value: unknown, maxDecimals = 2): string {
  return `${new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(safe(value))} %`;
}

/** Numero con separador de miles. */
export function formatNumber(value: unknown, decimals = 0): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  }).format(safe(value));
}

/** Cantidad de receta: hasta 3 decimales, sin ceros de relleno. */
export function formatQty(value: unknown, unit?: string): string {
  const text = new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(safe(value));
  return unit ? `${text} ${unit}` : text;
}

/** Importe con signo explicito, para variaciones y diferencias de precio. */
export function formatSignedMoney(value: unknown): string {
  const n = safe(value);
  const text = formatMoney(Math.abs(n));
  if (n > 0.005) return `+${text}`;
  if (n < -0.005) return `−${text}`;
  return text;
}

const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** "2026-03" -> "marzo de 2026" */
export function formatMonthLong(month: string): string {
  const [year, m] = String(month ?? '').split('-');
  const index = Number(m) - 1;
  if (!year || !MONTH_NAMES[index]) return String(month ?? '');
  return `${MONTH_NAMES[index]} de ${year}`;
}

/** "2026-03" -> "mar 26" (para ejes de graficas) */
export function formatMonthShort(month: string): string {
  const [year, m] = String(month ?? '').split('-');
  const index = Number(m) - 1;
  if (!year || !MONTH_NAMES[index]) return String(month ?? '');
  return `${MONTH_NAMES[index].slice(0, 3)} ${year.slice(2)}`;
}

/** "2026-03-14" -> "14/03/2026" */
export function formatDate(date: string): string {
  const parts = String(date ?? '').split('-');
  if (parts.length !== 3) return String(date ?? '');
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/**
 * "2026-08-25" -> "25 de agosto de 2026". Para fechas de LECTURA (fin de la
 * prueba, avisos): el formato largo se lee mejor que 25/08/2026 (ronda 3).
 */
export function formatDateLong(date: string): string {
  const parts = String(date ?? '').split('-');
  const index = Number(parts[1]) - 1;
  if (parts.length !== 3 || !MONTH_NAMES[index]) return formatDate(date);
  return `${Number(parts[2])} de ${MONTH_NAMES[index]} de ${parts[0]}`;
}

/** "2026-03-01" y "2026-08-31" -> "1 mar 2026 – 31 ago 2026" */
export function formatPeriod(start: string, end: string): string {
  return `${formatDayShort(start)} – ${formatDayShort(end)}`;
}

function formatDayShort(date: string): string {
  const parts = String(date ?? '').split('-');
  if (parts.length !== 3) return String(date ?? '');
  const index = Number(parts[1]) - 1;
  const name = MONTH_NAMES[index]?.slice(0, 3) ?? parts[1];
  return `${Number(parts[2])} ${name} ${parts[0]}`;
}

/**
 * Lee un numero escrito por el usuario aceptando coma o punto decimal.
 * Nunca devuelve NaN: si no se entiende, devuelve el valor por defecto.
 */
export function parseDecimal(value: string, fallback = 0): number {
  const normalized = String(value ?? '').trim().replace(/\s/g, '').replace(',', '.');
  if (normalized === '') return fallback;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : fallback;
}

/** Fecha de hoy en formato YYYY-MM-DD. */
export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Primera letra en mayuscula, para etiquetas que vienen en minusculas. */
export function capitalize(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}
